/**
 * Firebase service for workout routines
 */
import { ref, push, get, set, update, remove, onValue, off } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { database, storage } from './core';
import logger from '../../utils/logger';
import { 
  WorkoutRoutine, 
  WorkoutRoutineBase, 
  WorkoutRoutinePrivacy, 
  FavoriteWorkoutRoutine,
  WorkoutRoutineExercisePhoto
} from '../../types/workout-routine';

// Path helpers
const getWorkoutRoutinesPath = () => 'workoutRoutines';
const getWorkoutRoutinePreviewPath = (privacy: WorkoutRoutinePrivacy, userId: string) => 
  `workoutRoutinesPreview/${privacy}/${userId}`;
const getFavoriteRoutinesPath = (userId: string) => 
  `favouriteWorkoutRoutines/${userId}`;

/**
 * Validates if the privacy setting is valid
 */
const validateRoutine = ({ privacy }: { privacy: string }) => {
  if (!['public', 'private'].includes(privacy)) {
    throw new Error('Invalid "privacy" value supplied to API method');
  }
};

/**
 * Creates a new workout routine
 */
export const createWorkoutRoutine = async (
  userId: string,
  routineData: WorkoutRoutineBase
): Promise<string> => {
  validateRoutine(routineData);
  
  try {
    const routinesRef = ref(database, getWorkoutRoutinesPath());
    const newRoutineRef = push(routinesRef);
    
    if (!newRoutineRef.key) {
      throw new Error('Failed to generate routine key');
    }
    
    const currentTimestamp = Date.now();
    
    await set(newRoutineRef, {
      ...routineData,
      dateCreated: currentTimestamp,
      dateUpdated: currentTimestamp,
      author: userId
    });
    
    return newRoutineRef.key;
  } catch (error) {
    logger.error('Error creating workout routine:', error);
    throw error;
  }
};

/**
 * Updates an existing workout routine
 */
export const updateWorkoutRoutine = async (
  routineKey: string,
  updatedData: Partial<WorkoutRoutineBase>
): Promise<void> => {
  if (updatedData.privacy) {
    validateRoutine({ privacy: updatedData.privacy });
  }
  
  try {
    const routineRef = ref(database, `${getWorkoutRoutinesPath()}/${routineKey}`);
    
    await update(routineRef, {
      ...updatedData,
      dateUpdated: Date.now()
    });
  } catch (error) {
    logger.error('Error updating workout routine:', error);
    throw error;
  }
};

/**
 * Gets a specific workout routine by key
 */
export const getWorkoutRoutine = async (routineKey: string): Promise<WorkoutRoutine | null> => {
  try {
    const routineRef = ref(database, `${getWorkoutRoutinesPath()}/${routineKey}`);
    const snapshot = await get(routineRef);
    
    if (snapshot.exists()) {
      return {
        ...(snapshot.val() as WorkoutRoutineBase),
        routineKey
      };
    }
    
    return null;
  } catch (error) {
    logger.error('Error getting workout routine:', error);
    throw error;
  }
};

/**
 * Sets up a listener for a specific workout routine
 */
export const subscribeToWorkoutRoutine = (
  routineKey: string,
  callback: (routine: WorkoutRoutine) => void
): () => void => {
  const routineRef = ref(database, `${getWorkoutRoutinesPath()}/${routineKey}`);
  
  const handleSnapshot = (snapshot: any) => {
    if (snapshot.exists()) {
      const routineData: WorkoutRoutine = {
        ...(snapshot.val() as WorkoutRoutineBase),
        routineKey
      };
      callback(routineData);
    }
  };
  
  onValue(routineRef, handleSnapshot);
  
  // Return unsubscribe function
  return () => {
    off(routineRef);
  };
};

/**
 * Sets up a listener for a user's own workout routines of a specific privacy
 */
export const subscribeToOwnRoutines = (
  userId: string,
  privacy: WorkoutRoutinePrivacy,
  callback: (routines: Record<string, any>) => void
): () => void => {
  validateRoutine({ privacy });
  
  const routinesRef = ref(database, getWorkoutRoutinePreviewPath(privacy, userId));
  
  const handleSnapshot = (snapshot: any) => {
    if (snapshot.exists()) {
      callback(snapshot.val());
    } else {
      callback({});
    }
  };
  
  onValue(routinesRef, handleSnapshot);
  
  // Return unsubscribe function
  return () => {
    off(routinesRef);
  };
};

/**
 * Sets up a listener for all public workout routines
 */
export const subscribeToAllPublicRoutines = (
  callback: (routines: Record<string, any>) => void
): () => void => {
  const routinesRef = ref(database, 'workoutRoutinesPreview/public');
  
  const handleSnapshot = (snapshot: any) => {
    if (snapshot.exists()) {
      const routinesByUID = snapshot.val();
      const routines = Object.keys(routinesByUID).reduce((acc, uid) => {
        Object.entries(routinesByUID[uid]).forEach(([routineKey, routineData]: [string, any]) => {
          // Ensure we have a valid routine structure
          acc[routineKey] = {
            ...routineData,
            routineKey,
            sets: routineData.sets || [],
            tags: routineData.tags || [],
            likes: routineData.likes || {}
          };
        });
        return acc;
      }, {} as Record<string, any>);
      
      callback(routines);
    } else {
      callback({});
    }
  };
  
  onValue(routinesRef, handleSnapshot);
  
  // Return unsubscribe function
  return () => {
    off(routinesRef);
  };
};

/**
 * Fetches all public routine previews for a given user
 */
export const getPublicRoutinesPreviewForUser = async (
  userId: string
): Promise<Record<string, any>> => {
  try {
    const routinesRef = ref(database, `workoutRoutinesPreview/public/${userId}`);
    const snapshot = await get(routinesRef);
    
    if (snapshot.exists()) {
      return snapshot.val();
    }
    
    return {};
  } catch (error) {
    logger.error('Error getting public routines for user:', error);
    throw error;
  }
};

// Like functionality removed - use the unified likes.service.ts instead

/**
 * Gets the user's favorite workout routines
 */
export const getFavoriteRoutines = async (
  userId: string
): Promise<FavoriteWorkoutRoutine[]> => {
  try {
    const favoriteRoutinesRef = ref(database, getFavoriteRoutinesPath(userId));
    const snapshot = await get(favoriteRoutinesRef);
    
    if (snapshot.exists()) {
      return snapshot.val();
    }
    
    return [];
  } catch (error) {
    logger.error('Error getting favorite routines:', error);
    throw error;
  }
};

/**
 * Sets up a listener for a user's favorite workout routines
 */
export const subscribeToFavoriteRoutines = (
  userId: string,
  callback: (routines: FavoriteWorkoutRoutine[]) => void
): () => void => {
  const favoriteRoutinesRef = ref(database, getFavoriteRoutinesPath(userId));
  
  const handleSnapshot = (snapshot: any) => {
    if (snapshot.exists()) {
      callback(snapshot.val());
    } else {
      callback([]);
    }
  };
  
  onValue(favoriteRoutinesRef, handleSnapshot);
  
  // Return unsubscribe function
  return () => {
    off(favoriteRoutinesRef);
  };
};

/**
 * Updates a user's favorite workout routines
 */
export const setFavoriteRoutines = async (
  userId: string,
  favoriteRoutines: FavoriteWorkoutRoutine[]
): Promise<void> => {
  try {
    const favoriteRoutinesRef = ref(database, getFavoriteRoutinesPath(userId));
    await set(favoriteRoutinesRef, favoriteRoutines);
  } catch (error) {
    logger.error('Error setting favorite routines:', error);
    throw error;
  }
};

/**
 * Adds a cover photo to a workout routine
 */
export const addCoverPhoto = async (
  userId: string,
  routineKey: string,
  photoUri: string,
  aspectRatio?: number
): Promise<string> => {
  try {
    // Upload photo to Firebase Storage
    const fileName = 'img-cover-photo.jpg';
    const path = `photos/workoutRoutines/${userId}/${routineKey}/${fileName}`;
    const imageRef = storageRef(storage, path);
    
    // Convert URI to blob
    const response = await fetch(photoUri);
    const blob = await response.blob();
    
    // Upload blob
    await uploadBytes(imageRef, blob);
    
    // Get download URL
    const downloadURL = await getDownloadURL(imageRef);
    
    // Update routine in database with new cover photo URL
    const routineRef = ref(database, `${getWorkoutRoutinesPath()}/${routineKey}/coverPhoto`);
    await update(routineRef, { url: downloadURL, aspectRatio });
    
    return downloadURL;
  } catch (error) {
    logger.error('Error adding cover photo:', error);
    throw error;
  }
};

/**
 * Uploads an exercise photo to Firebase Storage and updates the database
 */
export const uploadExercisePhoto = async (
  userId: string,
  routineKey: string,
  setIndex: number,
  exerciseIndex: number,
  photoIndex: number,
  photoUri: string
): Promise<WorkoutRoutineExercisePhoto> => {
  try {
    // Generate random ID for the photo
    const randomId = Math.random().toString(36).substring(2, 12);
    const fileName = `img-exercise-${exerciseIndex}-${randomId}.jpg`;
    const path = `photos/workoutRoutines/${userId}/${routineKey}/${fileName}`;
    const imageRef = storageRef(storage, path);
    
    // Convert URI to blob
    const response = await fetch(photoUri);
    const blob = await response.blob();
    
    // Upload blob
    await uploadBytes(imageRef, blob);
    
    // Get download URL
    const downloadURL = await getDownloadURL(imageRef);
    
    // Create photo data
    const photoData: WorkoutRoutineExercisePhoto = {
      url: downloadURL,
      storageRef: path,
      dateAdded: Date.now()
    };
    
    // Update exercise in database with new photo
    const photoRef = ref(database, `${getWorkoutRoutinesPath()}/${routineKey}/sets/${setIndex}/exercises/${exerciseIndex}/photos/${photoIndex}`);
    await update(photoRef, photoData);
    
    return photoData;
  } catch (error) {
    logger.error('Error uploading exercise photo:', error);
    throw error;
  }
};

/**
 * Delete a workout routine
 * Removes the routine from both the main workoutRoutines node and the preview nodes
 * Also cleans up associated storage files
 */
export const deleteWorkoutRoutine = async (
  userId: string, 
  routineKey: string,
  privacy: WorkoutRoutinePrivacy = 'private'
): Promise<void> => {
  try {
    // First, fetch the routine to get storage references
    const routine = await getWorkoutRoutine(routineKey);
    
    if (routine) {
      // Clean up storage files
      const { deleteObject, ref: storageRef } = await import('firebase/storage');
      
      // Delete cover photo if exists
      if (routine.coverPhoto?.url) {
        try {
          const coverPhotoRef = storageRef(storage, `photos/workoutRoutines/${userId}/${routineKey}/img-cover-photo.jpg`);
          await deleteObject(coverPhotoRef);
        } catch (storageError) {
          logger.warn('Error deleting cover photo:', storageError);
          // Continue with deletion even if storage cleanup fails
        }
      }
      
      // Delete exercise photos if they exist
      if (routine.sets) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        for (const [_setIndex, set] of routine.sets.entries()) {
          if (set.exercises) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            for (const [_exerciseIndex, exercise] of set.exercises.entries()) {
              if (exercise.photos) {
                for (const photo of exercise.photos) {
                  if (photo.storageRef) {
                    try {
                      const photoRef = storageRef(storage, photo.storageRef);
                      await deleteObject(photoRef);
                    } catch (storageError) {
                      logger.warn(`Error deleting exercise photo: ${photo.storageRef}`, storageError);
                      // Continue with deletion even if individual photo cleanup fails
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    
    // Remove from main workoutRoutines node
    const routineRef = ref(database, `${getWorkoutRoutinesPath()}/${routineKey}`);
    await remove(routineRef);
    
    // Remove from preview node
    const previewRef = ref(database, `${getWorkoutRoutinePreviewPath(privacy, userId)}/${routineKey}`);
    await remove(previewRef);
    
    logger.info(`Successfully deleted workout routine ${routineKey}`);
  } catch (error) {
    logger.error('Error deleting workout routine:', error);
    throw error;
  }
};

/**
 * Duplicate a workout routine
 * Creates a copy of an existing routine with a new name
 */
export const duplicateWorkoutRoutine = async (
  userId: string,
  routineKey: string,
  newName?: string
): Promise<string> => {
  try {
    // Fetch the original routine
    const originalRoutine = await getWorkoutRoutine(routineKey);
    
    if (!originalRoutine) {
      throw new Error('Original workout routine not found');
    }
    
    // Create a copy of the routine data
    const { routineKey: _, author, dateCreated, dateUpdated, coverPhoto, ...routineData } = originalRoutine;
    
    // Create the duplicate with updated metadata
    const duplicateData: WorkoutRoutineBase = {
      ...routineData,
      title: newName || `${originalRoutine.title} (Copy)`,
      description: originalRoutine.description || '',
      author: userId, // Set the new user as the author
      privacy: originalRoutine.privacy || 'private',
      sets: originalRoutine.sets || [],
      tags: originalRoutine.tags || [],
      likes: {} // Reset likes for the copy
    };
    
    // Create the new routine
    const newRoutineKey = await createWorkoutRoutine(userId, duplicateData);
    
    // If the original had a cover photo, we don't copy it automatically
    // The user can add a new cover photo if desired
    
    logger.info(`Successfully duplicated workout routine ${routineKey} as ${newRoutineKey}`);
    return newRoutineKey;
  } catch (error) {
    logger.error('Error duplicating workout routine:', error);
    throw error;
  }
};